import {
  useMutation,
  UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';
import { OrganizationMember, UpdateOrganizationMemberRole } from '@tinct/core-api-models';
import { ApiError, tinctApi } from '../../api';
import { refreshMembersOnConflict } from './refresh-members-on-conflict';

export type UpdateOrganizationMemberRoleParams = {
  organizationId: string;
  memberId: string;
  data: UpdateOrganizationMemberRole;
};

export const updateOrganizationMemberRole = async (
  params: UpdateOrganizationMemberRoleParams,
): Promise<OrganizationMember> => {
  return await tinctApi.organizationMembers.updateOrganizationMemberRole(
    params.organizationId,
    params.memberId,
    params.data,
  );
};

export const useUpdateOrganizationMemberRole = (
  options?: Omit<
    UseMutationOptions<OrganizationMember, ApiError, UpdateOrganizationMemberRoleParams>,
    'mutationFn'
  >,
) => {
  const { onSuccess, onError, ...restOptions } = options || {};

  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateOrganizationMemberRole,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({
        queryKey: ['organization-members', args[1].organizationId],
      });
      queryClient.invalidateQueries({
        queryKey: ['organization-member', args[1].organizationId, args[1].memberId],
      });
      onSuccess?.(...args);
    },
    onError: (...args) => {
      refreshMembersOnConflict(queryClient, args[0], args[1].organizationId);
      onError?.(...args);
    },
    ...restOptions,
  });
};
